import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MessageCircle, Mail, HandCoins, Scale } from "lucide-react";
import { WHATSAPP_URL, WHATSAPP_DISPLAY, EMAIL } from "@/data/content";

const legalDocs = {
  privacidade: {
    title: "Política de Privacidade",
    body: "Este site coleta apenas os dados fornecidos voluntariamente no formulário de contato (nome, telefone, e-mail, assunto e mensagem), utilizados exclusivamente para responder ao seu contato, conforme a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018). Nenhum dado é vendido, compartilhado ou usado para outras finalidades. Você pode solicitar a exclusão dos seus dados a qualquer momento pelo e-mail oficial da campanha.",
  },
  termos: {
    title: "Termos de Uso",
    body: "Este é o site oficial de campanha de Rosangela da Saúde, candidata a Deputada Federal (PSB 40 — Nº 4030). Todo o conteúdo tem caráter informativo sobre a trajetória pública e as propostas da candidata, nos termos da legislação eleitoral vigente. A reprodução do conteúdo é permitida com citação da fonte. Não nos responsabilizamos por conteúdos de sites de terceiros aqui referenciados.",
  },
  aviso: {
    title: "Aviso de Propaganda Eleitoral",
    body: "Este site é um canal oficial de comunicação da campanha Rosangela da Saúde — 4030. Toda a propaganda eleitoral aqui veiculada segue a Lei nº 9.504/1997 e as resoluções do TSE, com identificação do comitê financeiro responsável (CNPJ 68.403.564/0001-09) no rodapé de todas as páginas. A prestação de contas está disponível no Portal DivulgaCandContas da Justiça Eleitoral.",
  },
};

export default function Footer() {
  const [doc, setDoc] = useState(null);

  return (
    <footer data-testid="footer" className="bg-brand-navy bg-noise pt-16 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 gap-12 pb-14 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-royal to-brand-dark">
                <svg viewBox="0 0 24 24" className="h-6 w-6 text-white" fill="currentColor" aria-hidden="true">
                  <path d="M12 3c-2.5 0-4.6 1.8-5.1 4.2-.2.9.3 1.6 1.2 1.6 2 0 3.9 1.1 3.9 3.2 0 1.4-1 2.6-2.4 2.9-2.9.7-5.1 2.9-5.6 5.8-.1.6.4 1.3 1.1 1.3h13.8c.7 0 1.2-.6 1.1-1.3C19.5 11.5 16.1 3 12 3z" />
                </svg>
                <span className="absolute -bottom-1 -right-1 rounded-md bg-brand-orange px-1.5 py-0.5 text-[9px] font-extrabold text-white">40</span>
              </span>
              <span className="leading-tight">
                <span className="block font-display text-base font-extrabold">Rosangela da Saúde</span>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-brand-light">Deputada Federal · 4030</span>
              </span>
            </div>
            <p className="mt-5 text-sm leading-relaxed text-white/70">
              Um mandato para cuidar e incluir. Saúde, educação e inclusão para o Extremo Sul da Bahia.
            </p>
          </div>

          <nav aria-label="Mapa do site">
            <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-brand-light">Navegação</h3>
            <ul className="space-y-2.5 text-sm">
              {[
                ["Sobre", "#sobre", "footer-link-sobre"],
                ["Prioridades", "#prioridades", "footer-link-prioridades"],
                ["Propostas", "#propostas", "footer-link-propostas"],
                ["Galeria", "#galeria", "footer-link-galeria"],
                ["Notícias", "#noticias", "footer-link-noticias"],
                ["Contato", "#contato", "footer-link-contato"],
              ].map(([label, href, testid]) => (
                <li key={href}>
                  <a href={href} data-testid={testid} className="text-white/70 transition-colors hover:text-brand-orange">{label}</a>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-brand-light">Contato</h3>
            <ul className="space-y-3 text-sm">
              <li>
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="footer-whatsapp" className="flex items-center gap-2 text-white/70 transition-colors hover:text-brand-orange">
                  <MessageCircle className="h-4 w-4" /> {WHATSAPP_DISPLAY}
                </a>
              </li>
              <li>
                <a href={`mailto:${EMAIL}`} data-testid="footer-email" className="flex items-center gap-2 break-all text-white/70 transition-colors hover:text-brand-orange">
                  <Mail className="h-4 w-4 flex-shrink-0" /> {EMAIL}
                </a>
              </li>
            </ul>
            <div className="mt-6">
              <h3 className="mb-2 font-display text-sm font-bold uppercase tracking-widest text-brand-light">Doações</h3>
              <p className="flex items-start gap-2 text-sm text-white/70" data-testid="footer-donations">
                <HandCoins className="mt-0.5 h-4 w-4 flex-shrink-0 text-brand-orange" />
                O canal oficial de doações autorizado pelo TSE será publicado aqui em breve. Enquanto isso,
                fale com a equipe pelo WhatsApp para saber como apoiar a campanha.
              </p>
            </div>
          </div>

          <nav aria-label="Documentos legais">
            <h3 className="mb-4 font-display text-sm font-bold uppercase tracking-widest text-brand-light">Transparência</h3>
            <ul className="space-y-2.5 text-sm">
              <li><button type="button" data-testid="footer-link-privacidade" onClick={() => setDoc("privacidade")} className="text-white/70 transition-colors hover:text-brand-orange">Política de Privacidade</button></li>
              <li><button type="button" data-testid="footer-link-termos" onClick={() => setDoc("termos")} className="text-white/70 transition-colors hover:text-brand-orange">Termos de Uso</button></li>
              <li><button type="button" data-testid="footer-link-aviso" onClick={() => setDoc("aviso")} className="text-white/70 transition-colors hover:text-brand-orange">Aviso de Propaganda Eleitoral</button></li>
              <li>
                <a
                  href="https://divulgacandcontas.tse.jus.br/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-link-divulgacand"
                  className="text-white/70 transition-colors hover:text-brand-orange"
                >
                  DivulgaCandContas (TSE)
                </a>
              </li>
            </ul>
          </nav>
        </div>

        <div data-testid="footer-compliance" className="rounded-t-3xl border-t border-white/10 bg-white/5 px-6 py-8 backdrop-blur-sm">
          <div className="flex items-start gap-3">
            <Scale className="mt-1 h-5 w-5 flex-shrink-0 text-brand-orange" aria-hidden="true" />
            <div className="space-y-2 text-xs sm:text-sm leading-relaxed text-white/75">
              <p className="font-bold text-white" data-testid="compliance-candidate-line">
                Rosangela da Saúde — Candidata a Deputada Federal — Nº 4030 — PSB
              </p>
              <p data-testid="compliance-cnpj">CNPJ do comitê financeiro: 68.403.564/0001-09</p>
              <p data-testid="compliance-disclaimer">
                Conteúdo produzido e custeado pelo Comitê Financeiro da candidata Rosangela da Saúde, em conformidade
                com a Lei nº 9.504/1997 e resoluções do TSE.
              </p>
              <p data-testid="compliance-precampaign">
                Propaganda eleitoral oficial da campanha 4030, realizada nos termos da Lei nº 9.504/1997 e das
                resoluções do TSE para as Eleições 2026.
              </p>
              <p data-testid="compliance-accounts">
                Prestação de contas disponível no Portal de Divulgação de Candidaturas e Contas Eleitorais
                (DivulgaCandContas) da Justiça Eleitoral.
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-center justify-between gap-3 border-t border-white/10 py-6 text-xs text-white/50 sm:flex-row">
          <p>© 2026 Rosangela da Saúde — PSB 40. Todos os direitos reservados.</p>
          <p>Bahia · Extremo Sul · Um mandato para cuidar e incluir</p>
        </div>
      </div>

      <AnimatePresence>
        {doc && (
          <motion.div
            data-testid="legal-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-brand-navy/70 p-4 backdrop-blur-sm"
            onClick={() => setDoc(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 12 }}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={legalDocs[doc].title}
              className="relative w-full max-w-lg rounded-3xl bg-white p-8 text-slate-800 shadow-2xl"
            >
              <button
                type="button"
                data-testid="legal-modal-close"
                aria-label="Fechar"
                onClick={() => setDoc(null)}
                className="absolute right-4 top-4 rounded-full p-2 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
              <h3 className="font-display text-xl font-extrabold text-slate-900">{legalDocs[doc].title}</h3>
              <p className="mt-4 text-sm leading-relaxed text-slate-600">{legalDocs[doc].body}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
}
